import React from 'react'
import styled from 'styled-components'
import { line, radius, font, surface } from 'features/dashboard/tokens'
import { scoreColour } from 'features/dashboard/charts/scoreColour'

const TrendBars = ({ points = [] }) => {
  const pts = points || []
  const withData = pts.filter(p => p.onTimeRate !== null && p.onTimeRate !== undefined)
  if (withData.length === 0) {
    return <Empty>No tasks have been completed in the last 6 months yet. Once tasks are completed, this will chart the share finished on or before their deadline, month by month.</Empty>
  }
  const latest = withData[withData.length - 1]
  const onTimeCount = Math.round((latest.onTimeRate / 100) * (latest.completed || 0))
  const anyEmpty = pts.some(p => p.onTimeRate === null || p.onTimeRate === undefined)

  return (
    <Wrap>
      <Head>
        <HeadValue $tint={scoreColour(latest.onTimeRate)}>{Math.round(latest.onTimeRate)}%</HeadValue>
        <HeadMeta>
          <HeadLabel>on time in {latest.label}</HeadLabel>
          <HeadSub>{onTimeCount} of {latest.completed} completed task{latest.completed === 1 ? '' : 's'} met the deadline</HeadSub>
        </HeadMeta>
      </Head>
      <Caption>Of the tasks completed each month, the share finished on or before their deadline. Taller, greener bars mean more work was done on time.</Caption>
      <Bars>
        {pts.map((p, i) => {
          const has = p.onTimeRate !== null && p.onTimeRate !== undefined
          return (
            <Col key={i} title={has ? `${p.label}: ${Math.round(p.onTimeRate)}% on time: ${p.completed} completed, ${p.due} due` : `${p.label}: no tasks completed`}>
              <Track>
                {has && <Fill style={{ height: `${Math.max(3, p.onTimeRate)}%`, background: scoreColour(p.onTimeRate) }} />}
              </Track>
              <ColLabel>{p.label}</ColLabel>
            </Col>
          )
        })}
      </Bars>
      {anyEmpty && <Foot>A blank column means no tasks were completed that month.</Foot>}
    </Wrap>
  )
}

export default TrendBars

const Wrap = styled.div`
  display: flex; flex-direction: column; gap: 12px;
`

const Head = styled.div`
  display: flex; align-items: center; gap: 10px;
`

const HeadMeta = styled.div`
  display: flex; flex-direction: column; gap: 1px; min-width: 0;
`

const HeadSub = styled.span`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Caption = styled.p`
  margin: 0;
  font-size: ${font.caption};
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Foot = styled.div`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
  font-style: italic;
`

const HeadValue = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 28px;
  letter-spacing: -0.8px;
  color: ${({ $tint }) => $tint};
  line-height: 1;
`

const HeadLabel = styled.span`
  font-size: ${font.caption};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Bars = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 8px;
  align-items: end;
  height: 120px;
`

const Col = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  height: 100%;
`

const Track = styled.div`
  flex: 1;
  width: 100%;
  display: flex; align-items: flex-end;
  background: ${surface.wash};
  border: 1px solid ${line.base};
  border-radius: ${radius.sm};
  overflow: hidden;
`

const Fill = styled.div`
  width: 100%;
  border-radius: ${radius.sm} ${radius.sm} 0 0;
`

const ColLabel = styled.span`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Empty = styled.div`
  padding: 24px; text-align: center;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: ${font.body};
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px dashed ${line.base};
  border-radius: ${radius.md};
`
